/**
 * # Badge
 *
 * A component that displays a badge with optional text and icon.
 *
 * ## Props
 *
 * - `children` (optional): The content to be displayed inside the badge.
 * - `className` (optional): Additional CSS class name for the badge.
 * - `color` (optional): The color variant of the badge. Possible values are `'passive'`, `'emphasized'`, `'progress'`, `'success'`, and `'danger'`.
 * - `icon` (optional): The icon to be displayed in the badge.
 * - `iconAlign` (optional): The alignment of the icon within the badge. Possible values are `'center'`, `'left'`, and `'right'`.
 * - `iconStyle` (optional): The style variant of the icon.
 * - `size` (optional): The size variant of the badge. Possible values are `'default'` and `'small'`.
 *
 * ## Usage
 *
 * ```tsx
 * import Badge from './Badge.react';
 *
 * const Example = () => {
 *   return (
 *     <Badge color="success" icon="check" iconAlign="right" size="small">
 *       New
 *     </Badge>
 *   );
 * };
 * ```
 */

import React from 'react';
import classnames from 'classnames';

import Text from '../Text/Text.react';
import Icon, {IconProp, IconStyle} from '../Icon/Icon.react';
import './badge.scss';

interface Props {
  children?: React.ReactNode;
  className?: string;
  color?: 'passive' | 'emphasized' | 'progress' | 'success' | 'danger';
  icon?: IconProp;
  iconAlign?: 'center' | 'left' | 'right';
  iconStyle?: IconStyle;
  size?: 'default' | 'small';
}

type RequireChildrenOrIcon = RequireAtLeastOne<Props, 'children' | 'icon'>;

export const Badge = ({
  children,
  className,
  color = 'passive',
  icon,
  iconAlign = 'left',
  iconStyle,
  size = 'default'
}: RequireChildrenOrIcon) => {
  const isPassive = color === 'passive';
  const emphasis = isPassive ? 'low' : 'high';
  const textColor = isPassive ? 'black' : 'white';

  return (
    <div
      className={classnames(
        'a-badge',
        {
          [`a-badge--color-${color}`]: color,
          [`a-badge--icon-${iconAlign}`]: icon && iconAlign,
          [`a-badge--size-${size}`]: size
        },
        className
      )}
    >
      {icon && (
        <Icon
          className='a-badge__icon'
          emphasis={emphasis}
          color={textColor}
          icon={icon}
          iconStyle={iconStyle}
        />
      )}
      <Text color={textColor} emphasis={emphasis} size='xs' bold>
        {children}
      </Text>
    </div>
  );
};

export default Badge;
